// src/app/services/payplus.service.ts
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import * as CryptoJS from 'crypto-js';
import { environment } from '../environments/environment';
import { Observable } from 'rxjs';

export type PayPlusMode = 'sandbox' | 'live';


export interface CustomerInfoDialogReturn {
  firstname?: string;
  lastname?: string;
  email?: string;
  phoneNumber?: string;
  dialCode?: string; // e.g. "+94"
  qty?:number;
}

export interface CustomerInfo {
  firstname?: string;
  lastname?: string;
  email?: string;
  phoneNumber?: string;
  dialCode?: string; // e.g. "+94"
}

export interface CreatePaymentSessionRequest {
  domain?: string;
  applicationKey?:string;
  amount?: number;                   // e.g. 4322.22
  paymentType?: 'ONE_TIME';          // per docs (extend if needed)
  orderId?: string;                  // your unique order id
  currency?: 'LKR';                  // per docs
  pluginVersion?: string;           // optional
  notifyUrl?: string;               // optional
  redirectUrl?: string;              // where to redirect after success
  source:
    | 'SPRINGBOOT' | 'JAVASCRIPT' | 'NEXT_JS' | 'ANGULAR_JS'
    | 'REACT_JS'   | 'NODE_JS'    | 'VUE_JS'  | 'DOT_NET'
    | 'LARAVEL';
  description?: string;
  doInitialPayment?: boolean;       // optional
  customerInfo?: CustomerInfo;
  platformType?:string;
}

export interface IResponseData {
  link?: string;
  token?: string;
}

export interface IPaymentResponse {
  result: "SUCCESS" | "FAILURE" | "PENDING";
  data: IResponseData;
}

export class PaymentResponse implements IPaymentResponse {
  result: "SUCCESS" | "FAILURE" | "PENDING";
  data: IResponseData;

  constructor(result: "SUCCESS" | "FAILURE" | "PENDING", data: IResponseData) {
    this.result = result;
    this.data = data;
  }
}



export interface PaymentStatusResponse {
  // Shape depends on gateway; keep lax for now
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class PayPlusService {
  private http = inject(HttpClient);

  private readonly cfg = environment.payplus;
  private readonly baseUrl =
    (this.cfg.mode as PayPlusMode) === 'sandbox'
      ? 'https://gateway-sandbox-api.payplus.lk'
      : 'https://gateway-api.payplus.lk';

  /** Encode UTF-8 -> Base64 (browser-safe) */
  private toBase64(obj: unknown): string {
    const json = JSON.stringify(obj);
    // Encode as UTF-8 bytes, then to Base64 (CryptoJS handles UTF-8 safely)
    const wordArray = CryptoJS.enc.Utf8.parse(json);
    return CryptoJS.enc.Base64.stringify(wordArray);
  }

  /** HMAC-SHA256(base64Payload, secret) -> hex */
  private signature(base64Payload: string): string {
    return CryptoJS.HmacSHA256(base64Payload, this.cfg.secret).toString(CryptoJS.enc.Hex);
  }

  /**
   * Create Payment Session
   * Sends a Base64-encoded payload as JSON string body and 'Authorization: hmac <signature>'
   */
  createPaymentSession(body: CreatePaymentSessionRequest) : Observable<HttpResponse<IPaymentResponse>> {
    const payload = { merchantId: this.cfg.merchantId, ...body };
    const base64Payload = this.toBase64(payload);
    const sig = this.signature(base64Payload);

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `hmac ${sig}`,
    });

    // NOTE: The docs show sending JSON.stringify(base64String)
    return this.http.post<IPaymentResponse>(
      `${this.baseUrl}/api/payment-session/create`,
      JSON.stringify(base64Payload),
      { headers ,observe:'response'}
    );
  }

  /**
   * Check Payment Status
   * Docs state the status request body is Base64 of: { merchantId, orderId }.
   */
  getPaymentStatus(orderId: string) {
    const base64Payload = this.toBase64({
      merchantId: this.cfg.merchantId,
      orderId,
    });

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    // Keeping consistent with create: send JSON.stringify(base64String)
    return this.http.post<PaymentStatusResponse>(
      `${this.baseUrl}/api/payment-session/status`,
      JSON.stringify(base64Payload),
      { headers }
    );
  }
}

// https://softnetcomputers.lk/softnet-computers-cart?orderStatus=SUCCESS&orderId=68bea84c8c53f91bb139daf3